import React, { useState, useEffect, useRef, useCallback } from 'react';
import PropTypes from 'prop-types';
import { Link } from 'gatsby';
import { Transition } from '@headlessui/react';

import { Stack } from '../../EveryLayout';
import { FadeInOpacity } from '../../Misc/FadeIn';
import { ListHoverItem } from '../';

import { headerClean } from '../../../helpers';

// Session storage key for preserving list state
const STORAGE_KEY = 'news-list-state';

// https://www.erichowey.dev/writing/load-more-button-and-infinite-scroll-in-gatsby/

const ViewNews = ({ articles }) => {
  // Ref for focus management - tracks index of first new article
  const firstNewItemRef = useRef(null);
  const listRef = useRef(null);

  // Get initial count from sessionStorage or default to 10
  // Only restore state when returning from an article (flag set on navigate)
  const getInitialCount = useCallback(() => {
    if (typeof window === 'undefined') return 10;
    try {
      // Check if we navigated away to an article (flag set in handleArticleClick)
      const navigatedAway = sessionStorage.getItem(`${STORAGE_KEY}-navigated`);

      if (!navigatedAway) {
        // Fresh page load - clear any stale saved state
        sessionStorage.removeItem(STORAGE_KEY);
        sessionStorage.removeItem(`${STORAGE_KEY}-scroll`);
        return 10;
      }

      // Clear the navigation flag - we've now returned
      sessionStorage.removeItem(`${STORAGE_KEY}-navigated`);

      const saved = sessionStorage.getItem(STORAGE_KEY);
      if (saved) {
        const { count } = JSON.parse(saved);
        return Math.min(count, articles.edges.length);
      }
    } catch (e) {
      // Ignore storage errors
    }
    return 10;
  }, [articles.edges.length]);

  // State for the list
  const [list, setList] = useState(() => {
    const initialCount = getInitialCount();
    return [...articles.edges.slice(0, initialCount)];
  });

  // State to trigger load more
  const [loadMore, setLoadMore] = useState(false);

  // State for loading indicator
  const [isLoading, setIsLoading] = useState(false);

  // State of whether there is more to load
  const [hasMore, setHasMore] = useState(articles.edges.length > 10);

  // State for screen reader announcement
  const [announcement, setAnnouncement] = useState('');

  // Track the previous list length for focus management
  const prevListLength = useRef(list.length);

  // Load more button click
  const handleLoadMore = () => {
    setIsLoading(true);
    setLoadMore(true);
  };

  // Handle loading more articles
  useEffect(() => {
    if (loadMore && hasMore) {
      const currentLength = list.length;
      const isMore = currentLength < articles.edges.length;
      const nextResults = isMore
        ? articles.edges.slice(currentLength, currentLength + 10)
        : [];

      // Store previous length for focus management
      prevListLength.current = currentLength;

      setList([...list, ...nextResults]);
      setLoadMore(false);
      setIsLoading(false);

      // Announce to screen readers
      const newCount = nextResults.length;
      const totalCount = currentLength + newCount;
      setAnnouncement(
        `${newCount} more articles loaded. Showing ${totalCount} of ${articles.edges.length} articles.`
      );
    }
  }, [loadMore, hasMore]); //eslint-disable-line

  // Check if there is more
  useEffect(() => {
    const isMore = list.length < articles.edges.length;
    setHasMore(isMore);
  }, [list]); //eslint-disable-line

  // Save state to sessionStorage when list changes
  useEffect(() => {
    if (typeof window === 'undefined') return;
    try {
      sessionStorage.setItem(
        STORAGE_KEY,
        JSON.stringify({ count: list.length })
      );
    } catch (e) {
      // Ignore storage errors
    }
  }, [list.length]);

  // Restore scroll position on mount
  useEffect(() => {
    if (typeof window === 'undefined') return;
    try {
      const savedScroll = sessionStorage.getItem(`${STORAGE_KEY}-scroll`);
      if (savedScroll) {
        const scrollY = parseInt(savedScroll, 10);
        // Use requestAnimationFrame to ensure DOM is ready
        requestAnimationFrame(() => {
          window.scrollTo(0, scrollY);
        });
        sessionStorage.removeItem(`${STORAGE_KEY}-scroll`);
      }
    } catch (e) {
      // Ignore storage errors
    }
  }, []);

  // Focus management - focus first new item after load
  useEffect(() => {
    if (list.length > prevListLength.current && firstNewItemRef.current) {
      // Small delay to ensure transition has started
      setTimeout(() => {
        if (firstNewItemRef.current) {
          firstNewItemRef.current.focus();
        }
      }, 100);
    }
  }, [list.length]);

  // Save scroll position and set navigation flag before navigating to an article
  const handleArticleClick = () => {
    if (typeof window === 'undefined') return;
    try {
      sessionStorage.setItem(`${STORAGE_KEY}-scroll`, window.scrollY.toString());
      sessionStorage.setItem(`${STORAGE_KEY}-navigated`, 'true');
    } catch (e) {
      // Ignore storage errors
    }
  };

  // Calculate remaining articles for button label
  const remainingArticles = articles.edges.length - list.length;

  return (
    <FadeInOpacity>
      <Stack>
        {/* ARIA live region for screen reader announcements */}
        <div
          role="status"
          aria-live="polite"
          aria-atomic="true"
          className="sr-only"
        >
          {announcement}
        </div>

        <div aria-hidden="true" className="hidden md:grid grid-cols-12">
          <div className="col-span-3 md:col-span-4 xl:col-span-3 2xl:col-span-2">
           {/* ? > 40px, lh ? > 46px */}
           <span className="text-lg 2xl:text-[2.5rem] leading-[1.15]">Date</span>
          </div>
          {/*
          <div className="col-span-3 md:col-span-2 xl:col-span-2">
            <span className="text-lg 2xl:text-[2.5rem] leading-[1.15]">Type</span>
          </div>
          */}
          <div className="col-span-6 xl:col-span-7 2xl:col-span-8">
            <span className="text-lg 2xl:text-[2.5rem] leading-[1.15]">Headline</span>
          </div>
        </div>
        <ul
          ref={listRef}
          className="w-full divide-y divide-color-base divide-opacity-20 border-y border-color-base border-opacity-20"
          aria-label="News articles"
        >
          {list.map(({ node }, i) => {
            // Set ref on first item of newly loaded batch for focus management
            const isFirstNewItem = i === prevListLength.current;
            return (
              <Transition
                as="li"
                key={`news-listing-${node.uid || i}`}
                show={true}
                appear={true}
                enter="transition ease-out duration-200"
                enterFrom="opacity-0 "
                enterTo="opacity-100"
                leave="transition ease-in duration-150"
                leaveFrom="opacity-100"
                leaveTo="opacity-0"
                className="opacity-0"
              >
                <ListRow
                  node={node}
                  onNavigate={handleArticleClick}
                  ref={isFirstNewItem ? firstNewItemRef : null}
                />
              </Transition>
            );
          })}
        </ul>
        <div>
          {/* ? > 21px, lh ? > 25.84px */}
          {hasMore ? (
            <button
              onClick={handleLoadMore}
              disabled={isLoading}
              aria-busy={isLoading}
              className="text-[1.3rem] italic link no-underline disabled:opacity-50"
            >
              {isLoading ? (
                'Loading...'
              ) : (
                `+ Load more articles (${remainingArticles} remaining)`
              )}
            </button>
          ) : (
            <p className="prose prose-custom text-sm"><em>All {articles.edges.length} articles shown.</em></p>
          )}
        </div>
      </Stack>
    </FadeInOpacity>
  );
};

const ListRow = React.forwardRef(({ node, onNavigate }, ref) => {
  const headline = headerClean(node.data.title.text);
  const publishedDate = node.data.date || node.first_publication_date;
  return (
    <Link
      ref={ref}
      to={`/news/${node.uid}/`}
      onClick={onNavigate}
      className="px-2 block hover-row no-underline hover:bg-off-white focus:outline-none focus:ring-2 focus:ring-blue focus:ring-offset-2"
      aria-label={`${node.data.title.text}${node.data.category ? ` (${node.data.category})` : ''}, ${publishedDate}`}
    >
      <div className="grid grid-cols-4 md:grid-cols-12 py-1">
        <div className="h-full flex items-center col-span-3 md:col-span-4 xl:col-span-3 2xl:col-span-2 pb-2 md:pb-0">
          <ListHoverItem>
            {/* ? > 18px, lh ? > 22px */}
            <time className="font-trade text-[1rem] sm:text-xs 2xl:text-[1.15rem]">
              {publishedDate}
            </time>
          </ListHoverItem>
        </div>
        {node.data.category && (
          <div className="h-full flex justify-end md:justify-start items-center col-span-1 md:col-span-2 xl:col-span-2 pb-2 md:pb-0">
            <span className="font-trade text-[1rem] sm:text-xs 2xl:text-[1.15rem] md:min-w-[10rem]" aria-hidden="true">
              {node.data.category || 'News'}
            </span>
          </div>
        )}
        <div className="col-span-4 md:col-span-6 xl:col-span-7 2xl:col-span-8 pb-6 md:pb-0">
          {/* ? > 18px, lh ? > 22px */}
          <span
            className="text-[1rem] sm:text-xs 2xl:text-[1.15rem]s"
            aria-hidden="true"
            dangerouslySetInnerHTML={{ __html: headline }}
          />
        </div>
      </div>
    </Link>
  );
});

ListRow.displayName = 'ListRow';

ViewNews.propTypes = {
  articles: PropTypes.shape({}).isRequired,
};

ListRow.propTypes = {
  node: PropTypes.shape({}).isRequired,
  onNavigate: PropTypes.func,
};

export default ViewNews;
